import React, {
  useEffect, useRef, KeyboardEvent, CSSProperties
} from 'react';
import {
  Control,
  Controller,
  useFieldArray,
  ArrayPath,
  FieldValues,
  Path,
  FieldArray,
} from 'react-hook-form';
import {
  mergeRefs
} from 'react-laag';

import {
  themeColors
} from 'src/shared/utils';

import * as Style from './InputWithTags.style';

const placeholderDefaultStyles: CSSProperties = {
  fontSize: '17px',
  lineHeight: '26px',
  color: themeColors['dim-gray'],
  fontWeight: 500,
};

const fieldDefaultStyles: CSSProperties = {
  fontSize: '17px',
  lineHeight: '26px',
  color: themeColors['dark-gray'],
  fontWeight: 500,
  padding: '0 12px',
  backgroundColor: themeColors['list-blue'],
  borderRadius: '123px',
};

const inputButtonDefaultStyles: CSSProperties = {
  width: '100%',
  gap: '12px',
  padding: '11px 15px',
  backgroundColor: themeColors['alice-blue'],
  borderRadius: '4px',
  minHeight: '50px',
};

interface InputWithTagsProps<T extends FieldValues, K extends ArrayPath<T>> {
  control: Control<T>;
  name: K;
  objectKey: keyof T[K][number];
  errorMessage?: string | null;
  placeholder: string;
  onFieldValidate?: () => void;
  pastedTextSplitter?: (value: string) => string[];
  placeholderCustomStyles?: CSSProperties;
  fieldCustomStyles?: CSSProperties;
  inputButtonCustomStyle?: CSSProperties;
}

export const InputWithTags = <T extends FieldValues, K extends ArrayPath<T>>({
  control,
  name,
  errorMessage,
  placeholder,
  onFieldValidate,
  pastedTextSplitter,
  objectKey,
  placeholderCustomStyles,
  fieldCustomStyles,
  inputButtonCustomStyle,
}: InputWithTagsProps<T, K>) => {
  const fieldsRef = useRef<HTMLSpanElement[]>([]);

  const {
    fields, append, remove
  } = useFieldArray({
    control,
    name,
  });

  const addNewField = (newValue = '') => {
    append({
      [objectKey]: newValue,
    } as FieldArray<T, K>);

    if (onFieldValidate) {
      onFieldValidate();
    }
  };

  const handleKeyDown = (
    event: KeyboardEvent<HTMLSpanElement>,
    index: number,
  ) => {
    if (['Space', 'Enter', 'Tab', 'Comma'].includes(event.code)) {
      event.preventDefault();

      if (index !== fields.length - 1) {
        event.currentTarget.blur();

        return;
      }

      addNewField();
    }
  };

  const onBlur = (value: string, index: number) => {
    if (!value) {
      remove(index);
    }

    if (onFieldValidate) {
      onFieldValidate();
    }
  };

  const handlePaste = async (
    event: React.ClipboardEvent<HTMLSpanElement>,
    index: number,
  ) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData('text');

    if (!pastedTextSplitter) {
      addNewField(pastedText);

      return;
    }

    const textToAdd = pastedTextSplitter(pastedText);

    remove(index);

    textToAdd.forEach((value) => {
      addNewField(value);
    });
  };

  useEffect(
    () => {
      fieldsRef.current.forEach((element, index) => {
        if (!element) {
          return;
        }

        if (element.textContent !== fields[index][objectKey]) {
          element.textContent = fields[index][objectKey] as string;
        }
      });
    },
    [fields, objectKey]
  );

  return (
    <Style.InputButton
      onClick={(event) => {
        event.stopPropagation();

        const {
          nodeName
        } = event.target as HTMLButtonElement;

        if (nodeName !== 'SPAN') {
          addNewField();
        }
      }}
      tabIndex={0}
      aria-label="add new tag"
      type="button"
      style={{
        ...inputButtonDefaultStyles,
        ...inputButtonCustomStyle,
      }}
    >
      {fields.map((field, index) => (
        <div key={field.id}>
          <Controller
            name={`${name}.${index}.${String(objectKey)}` as Path<T>}
            control={control}
            render={({
              field: inputField
            }) => (
              <Style.EmailField
                role="textbox"
                contentEditable
                onKeyDown={(event) => handleKeyDown(
                  event,
                  index
                )}
                onInput={(event) => {
                  inputField.onChange(event.currentTarget.textContent);
                }}
                onBlur={() => onBlur(
                  inputField.value,
                  index
                )}
                tabIndex={0}
                ref={mergeRefs(
                  inputField.ref,
                  (el: HTMLSpanElement) => {
                    fieldsRef.current[index] = el;
                  }
                )}
                onPaste={(event) => handlePaste(
                  event,
                  index
                )}
                defaultValue={inputField.value}
                style={{
                  ...fieldDefaultStyles,
                  ...fieldCustomStyles,
                }}
              />
            )}
          />
        </div>
      ))}

      {!fields.length && (
        <p
          style={{
            ...placeholderDefaultStyles,
            ...placeholderCustomStyles,
          }}
        >
          {placeholder}
        </p>
      )}

      {errorMessage && <Style.ErrorMessage>{errorMessage}</Style.ErrorMessage>}
    </Style.InputButton>
  );
};
